import { beginArticleLinksNavigation } from '@/redux/modules/articleLinks/articleLinks.actions';
import { fetchArticleLinksIfCurrent } from '@/redux/modules/articleLinks/articleLinks.loader';
import { ARTICLE_LINKS_LIMIT } from '@/redux/modules/articleLinks/articleLinks.types';
import {
    clearBrowseSeoMeta,
    setBrowseBaseCategoryIds,
    setBrowseSeoMeta,
} from '@/redux/modules/browsePath/browsePath.reducer';
import { getCategoryEntries } from '@/redux/modules/category/category.selectors';
import { getCatalogIdsForCategory } from '@/redux/modules/categoryCatalogCards';
import { fetchCombinationCategory } from '@/redux/modules/combinationCategory';
import { getDeployment } from '@/redux/modules/config';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { getItemSummarySellerId, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { DEFAULT_SORT_ORDER } from '@/redux/modules/search/search.types';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { fetchSimilarItemsIfNeeded, getSimilarItemIds } from '@/redux/modules/similarItems';
import { FilterAction } from '@/types/SearchFacet';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getPreservedRedirectParams } from '@/utils/queryParams';
import { getCategoryIdsFromPathname, getCategoryNameFromPathname, urlSafeTitle } from '@/utils/urls';

import { runPreloadPhase, runSearchPhase, runSecondaryPhase } from './categorySearchData/asyncPhases';
import { resolveBrowseInputs } from './categorySearchData/browseResolution';
import { buildCategorySearchQuery, DEFAULT_PRICE_GUIDE_SORT } from './categorySearchData/queryBuilders';
import {
    getCanonicalBrowseRedirectUrl,
    getEarlyBrowseRedirectUrl,
    getLegacyQuadBrowseRedirectUrl,
} from './categorySearchData/redirects';
import { buildRouteContext } from './categorySearchData/routeContext';
import { CategoryEntryLite, CategoryLoaderParams } from './categorySearchData/types';

const getLandingPageIds = (routeContext: ReturnType<typeof buildRouteContext>, baseCategoryIds: number[]) => {
    if (routeContext.legacyPathParsed) {
        return [Number(routeContext.legacyPathParsed.numericCategoryId)];
    }
    if (routeContext.isBrowseSlugRoute) {
        return baseCategoryIds;
    }
    if (routeContext.isCombinationCategory) {
        return [routeContext.legacyCategoryId, routeContext.legacyCategoryId2];
    }
    if (routeContext.hasRegularLegacyCategory) {
        return [routeContext.legacyCategoryId];
    }
    return [];
};

export default ({ amplitudeExperimentFlags, location, match, redirect, routerState, store }: CategoryLoaderParams) => {
    const { params } = match;
    const { dispatch: rawDispatch, getState } = store;
    const dispatch = (action: any) => (rawDispatch as any)(action);
    const articleLinksNavigationId = rawDispatch(beginArticleLinksNavigation());

    const routeContext = buildRouteContext({
        location: { pathname: location.pathname, search: location.search },
        params,
    });

    const earlyBrowseRedirectUrl = getEarlyBrowseRedirectUrl({
        browseParsed: routeContext.browseParsed,
        pathname: routeContext.pathname,
        queryParams: routeContext.queryParams,
    });
    if (earlyBrowseRedirectUrl) {
        return redirect(earlyBrowseRedirectUrl, true);
    }

    const canonicalBrowseRedirectUrl = getCanonicalBrowseRedirectUrl({
        locationSearch: routeContext.locationSearch,
        pathname: routeContext.pathname,
    });
    if (canonicalBrowseRedirectUrl) {
        return redirect(canonicalBrowseRedirectUrl, true);
    }

    const legacyQuadBrowseRedirectUrl = getLegacyQuadBrowseRedirectUrl({
        locationSearch: routeContext.locationSearch,
        pathname: routeContext.pathname,
    });
    if (legacyQuadBrowseRedirectUrl) {
        return redirect(legacyQuadBrowseRedirectUrl, true);
    }

    return runPreloadPhase({
        dispatch,
        isCombinationCategory: routeContext.isCombinationCategory,
        itemId: routeContext.itemId,
        legacyCategoryId2: routeContext.legacyCategoryId2,
        params,
        pathname: routeContext.pathname,
        queryParams: routeContext.queryParams,
    }).then(async ({ combinationIds }) => {
        dispatch(clearBrowseSeoMeta());

        const stateAfterFacets = getState();
        const deployment = String(getDeployment(stateAfterFacets) || '');

        const browseResolution = await resolveBrowseInputs({
            deployment,
            locationSearch: routeContext.locationSearch,
            routeContext,
            searchFacets: getSearchFacets(stateAfterFacets),
            stateAfterFacets,
        });
        if (browseResolution.redirectUrl) {
            return redirect(browseResolution.redirectUrl, true);
        }

        const { baseCategoryIds, browseFacetOptionIds } = browseResolution;

        const browseSlugForState =
            routeContext.isBrowseSlugRoute && params.browseCategorySlug ? params.browseCategorySlug : null;
        dispatch(
            setBrowseBaseCategoryIds({
                ids: baseCategoryIds,
                browseCategorySlug: browseSlugForState,
                browsePathname: routeContext.pathname,
            })
        );
        const isBrowseRoute = !routeContext.archivedSearch && routeContext.pageName === 'category';
        if (isBrowseRoute && browseResolution.seo) {
            dispatch(setBrowseSeoMeta(browseResolution.seo));
        } else {
            dispatch(clearBrowseSeoMeta());
        }

        const finalCategoryIdForTiles = baseCategoryIds.length ? baseCategoryIds[baseCategoryIds.length - 1] : 0;
        const adsCategoryKey = baseCategoryIds.length
            ? baseCategoryIds.join(',')
            : getCategoryIdsFromPathname(routeContext.pathname);
        /*
         * The route context builds `relatedSearchTilesParams` before browse resolution runs, so for
         * browse-slug routes whose top-cat slug is not in the hardcoded alias map (e.g. `/c/chairs/`),
         * `categoryIds` comes out empty. Patch with the browse-resolved ids so the tiles fetch always
         * has a valid category id.
         */
        const relatedSearchTilesParams =
            routeContext.relatedSearchTilesParams.categoryIds.length === 0 && finalCategoryIdForTiles > 0
                ? { ...routeContext.relatedSearchTilesParams, categoryIds: [finalCategoryIdForTiles] }
                : routeContext.relatedSearchTilesParams;
        const category =
            params.browseCategorySlug?.replace(/-/g, ' ') || getCategoryNameFromPathname(routeContext.pathname);
        await runSecondaryPhase({
            adsCategoryKey,
            dispatch,
            finalCategoryIdForTiles,
            itemId: routeContext.itemId,
            keywords: routeContext.keywords,
            pageName: routeContext.pageName,
            pathname: routeContext.pathname,
            relatedSearchTilesParams,
            searchTerm: category,
            shouldFetchCategoryBanner: baseCategoryIds.length > 0,
        });

        await fetchArticleLinksIfCurrent({
            amplitudeExperimentFlags,
            dispatch: rawDispatch,
            getState,
            navigationId: articleLinksNavigationId,
            params: category ? { category, searchLimit: ARTICLE_LINKS_LIMIT } : undefined,
        });

        const currentState = getState();
        const defaultSortOrder = routeContext.archivedSearch ? DEFAULT_PRICE_GUIDE_SORT : DEFAULT_SORT_ORDER;
        const pageSize = getSearchPageSize(currentState);
        const catalogIds = getCatalogIdsForCategory(currentState);
        const rawFacets = getSearchFacets(currentState);
        const { searchQuery } = buildCategorySearchQuery({
            browseFacetOptionIds,
            defaultSortOrder,
            pathname: routeContext.pathname,
            queryParams: routeContext.queryParams,
            rawFacets,
            searchPageSize: pageSize,
        });

        const landingPageIds = getLandingPageIds(routeContext, baseCategoryIds);
        const nonFilterActions: FilterAction[] = [
            FilterAction.PageChanged,
            FilterAction.PageSizeChanged,
            FilterAction.SortByChanged,
            FilterAction.ViewPriceResults,
            FilterAction.ViewUpcomingItems,
        ];
        const isSSR = !routerState || typeof window === 'undefined';
        const isFilterRefinement =
            Boolean(routerState?.filterAction) && !nonFilterActions.includes(routerState?.filterAction);
        const needsAggs = isSSR || isFilterRefinement;

        await runSearchPhase({
            catalogIds,
            dispatch,
            landingPageIds,
            needsAggs,
            searchQuery,
        });

        const searchState = getState();
        const itemIds = getSearchedItemIds(searchState);
        const followUpPromises: Promise<any>[] = [];
        const preservedParams = getPreservedRedirectParams(routeContext.queryParams);

        // A 403 (deep page requires auth) also leaves itemIds empty; that case renders the
        // sign-in interstitial instead of being redirected like a genuine zero-results/out-of-range page.
        const { deepPageRequiresAuth } = getSearchUiState(searchState);
        if (!deepPageRequiresAuth && itemIds.length === 0 && routeContext.queryParams?.page > 0) {
            redirect?.(`${location.pathname}${preservedParams}`, true);
            return;
        }

        if (!deepPageRequiresAuth && itemIds.length === 0 && combinationIds) {
            const category = (Object.values(getCategoryEntries(searchState)) as CategoryEntryLite[])
                .filter((entry) => entry.facetId === 1)
                .pop();

            if (category?.name && category?.categoryId) {
                redirect?.(`/c/${urlSafeTitle(category.name)}/${category.categoryId}${preservedParams}`, true);
                return;
            }
        }

        if (combinationIds) {
            followUpPromises.push(dispatch(fetchCombinationCategory(combinationIds)));
        }

        if (itemIds?.length) {
            if (params?.deleted && !Number.isNaN(params.deleted)) {
                followUpPromises.push(
                    dispatch(
                        fetchItemSummariesIfNeeded({
                            identifier: 'MY-SAVED-SEARCH-PAGE-DELETED',
                            itemIds: [params.deleted],
                        })
                    )
                );
            }

            await Promise.all(followUpPromises);
            return;
        }

        const { soldItemIds = [] } = getSearchUiState(searchState);
        followUpPromises.push(
            dispatch(
                fetchItemSummariesIfNeeded({
                    identifier: 'MY-SAVED-SEARCH-PAGE-SOLD',
                    itemIds: soldItemIds,
                })
            )
        );
        followUpPromises.push(dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])));

        await Promise.all(followUpPromises);

        const stateAfterSold = getState();
        const soldItemId = soldItemIds[0];
        const sellerId = getItemSummarySellerId(stateAfterSold, soldItemId);
        const title = getItemSummaryTitle(stateAfterSold, soldItemId);
        const facets = getItemFacets(stateAfterSold, soldItemId);
        const categoryIds = getFacetIds(facets);

        await Promise.all([
            dispatch(
                fetchSimilarItemsIfNeeded({
                    categoryIds,
                    itemId: soldItemId,
                    sellerId,
                    title,
                })
            ),
        ]);

        const stateAfterSimilar = getState();
        const similarItemIds = getSimilarItemIds(stateAfterSimilar, soldItemId);
        await dispatch(
            fetchItemSummariesIfNeeded({
                identifier: 'MY-SAVED-SEARCH-PAGE-SIMILAR',
                itemIds: similarItemIds,
            })
        );
    });
};
